import {
  decodePairingToken,
  type ChannelType,
  type ControlPayload,
  type DeviceInfo,
  type EncryptedEnvelope,
  type FileMeta,
  type FileOfferPayload,
  type ImagePayload,
  type KeyDistribution,
  type MetaPayload,
  type ServerToClient,
  type TextPayload,
} from "@zeroclipboard/shared";
import {
  loadOrCreateIdentity,
  deleteIdentity,
  type DeviceIdentity,
} from "./crypto/identity";
import {
  createPairingMaterial,
  deriveEffectiveSecret,
  keyBinding as computeKeyBinding,
  pairingHash,
  sasCode,
} from "./crypto/pairing";
import { base64UrlToBytes, bytesToBase64Url } from "@zeroclipboard/shared";
import { SIZE_LIMITS, utf8ToBytes } from "@zeroclipboard/shared";
import { ChannelEngine } from "./engine";
import { ApiClient, WsClient } from "./net";
import { encodeQr, renderQrSvg } from "./qr";
import {
  cycleTheme,
  getThemePref,
  initTheme,
  themeIcon,
  themeLabel,
} from "./theme";
import {
  getChannel,
  getReplayState,
  listChannels,
  removeChannel,
  saveChannel,
  saveReplayState,
  touchChannel,
  type SavedChannel,
} from "./channels";

const $ = <T extends HTMLElement = HTMLElement>(id: string): T =>
  document.getElementById(id) as T;

// Relay origin, baked at build time via esbuild `define`. Empty string = same
// origin (Worker-served app / local dev). Set ZC_RELAY_BASE at build time so the
// Pages app (clip0.pages.dev) talks to the relay Worker on its own origin.
declare const __ZC_RELAY_BASE__: string | undefined;
const RELAY_BASE =
  typeof __ZC_RELAY_BASE__ === "string" ? __ZC_RELAY_BASE__ : "";

const api = new ApiClient(RELAY_BASE);

let identity: DeviceIdentity;
let engine: ChannelEngine | null = null;
let ws: WsClient | null = null;
let wsOpen = false;
let pollTimer: number | null = null;
let activeChannelId = "";
let activeSecret: Uint8Array | null = null;
let activeChannel: SavedChannel | null = null;
/** Files this device has offered (fileId -> File), streamed only on request. */
const outgoingFiles = new Map<string, File>();
/** When true, copying anything on this device auto-sends it to the channel. */
let autoSync = true;
/** Signature of the last clipboard content we sent OR applied, so auto-capture
 *  never re-uploads the same clipboard (and never echoes an incoming item). */
let lastAutoSig = "";

/* --------------------------------- boot ---------------------------------- */

async function main(): Promise<void> {
  initTheme();
  const themeBtn = $("theme-toggle");
  const renderTheme = (pref = getThemePref()): void => {
    themeBtn.innerHTML =
      `<span class="theme-ico">${themeIcon(pref)}</span>` +
      `<span class="theme-label">${themeLabel(pref)}</span>`;
  };
  renderTheme();
  themeBtn.addEventListener("click", () => renderTheme(cycleTheme()));

  // The device identity is a LOCAL key (IndexedDB); creating it does NOT touch
  // the server. No server resource is created until you create/join a channel.
  try {
    identity = await loadOrCreateIdentity();
  } catch (e) {
    setStatus("no X25519 WebCrypto support", "error");
    log(String(e), "err");
    return;
  }
  setStatus("device " + identity.deviceId, "ok");
  log(`device id: ${identity.deviceId}`);

  $("btn-create").addEventListener("click", () => void createChannel());
  $("btn-join").addEventListener("click", () => void joinChannel());
  $("btn-copy-token").addEventListener("click", copyToken);
  $("btn-copy-code").addEventListener("click", copyCode);
  $("btn-send-text").addEventListener("click", () => void sendText());
  $("btn-read-clip").addEventListener("click", () => void sendFromClipboard());
  $("btn-clear").addEventListener("click", () => void sendClear());
  $("btn-rotate").addEventListener("click", () => void rotateKeys());
  $("btn-lock").addEventListener("click", () => void toggleLock());
  $("btn-leave").addEventListener("click", () => void leaveChannel());
  $("btn-expire").addEventListener("click", () => void expireActiveChannel());
  $<HTMLInputElement>("file-input").addEventListener("change", onFilePicked);
  wireDropzone();
  document.addEventListener("paste", (e) => void onPaste(e));

  // Auto-sync (point 1): when enabled, reading the clipboard on focus / tab
  // re-visibility captures whatever was just copied and sends it. A permission
  // prompt may appear; if the user declines, the read fails and we stay quiet.
  const autosyncEl = $<HTMLInputElement>("chk-autosync");
  autoSync = autosyncEl.checked;
  autosyncEl.addEventListener("change", () => {
    autoSync = autosyncEl.checked;
    if (autoSync) void captureAndSend(false);
  });
  window.addEventListener("focus", () => {
    if (autoSync) void captureAndSend(false);
  });
  document.addEventListener("visibilitychange", () => {
    if (autoSync && document.visibilityState === "visible")
      void captureAndSend(false);
  });

  window.addEventListener("hashchange", () => void handleRoute());
  await handleRoute();
}

/* -------------------------------- routing -------------------------------- */

const VIEWS = [
  "view-home",
  "view-channels",
  "view-new",
  "session-panel",
  "view-privacy",
  "view-security",
  "view-terms",
];

function showView(id: string): void {
  for (const v of VIEWS) $(v).classList.toggle("hidden", v !== id);
  const navFor: Record<string, string> = {
    "view-home": "/",
    "view-channels": "/channels",
    "view-new": "/new",
  };
  for (const a of document.querySelectorAll<HTMLAnchorElement>("[data-nav]")) {
    a.classList.toggle("active", a.getAttribute("data-nav") === navFor[id]);
  }
  try {
    window.scrollTo(0, 0);
  } catch {
    /* not implemented in some environments (jsdom) */
  }
}

function navigate(to: string): void {
  if (location.hash === "#" + to) void handleRoute();
  else location.hash = "#" + to;
}

async function handleRoute(): Promise<void> {
  const path = location.hash.replace(/^#/, "") || "/";

  const joinMatch = /^\/join\/(.+)$/.exec(path);
  if (joinMatch) {
    teardownSession();
    $("pair-result").classList.add("hidden");
    showView("view-new");
    prefillJoin(decodeURIComponent(joinMatch[1]!));
    return;
  }

  const openMatch = /^\/c\/([^/]+)$/.exec(path);
  if (openMatch) {
    await openChannel(decodeURIComponent(openMatch[1]!));
    return;
  }

  if (path === "/channels") {
    teardownSession();
    showView("view-channels");
    await renderChannelsList();
    return;
  }
  if (path === "/new") {
    teardownSession();
    $("pair-result").classList.add("hidden");
    showView("view-new");
    return;
  }
  if (path === "/privacy" || path === "/security" || path === "/terms") {
    teardownSession();
    showView(`view-${path.slice(1)}`);
    return;
  }
  teardownSession();
  showView("view-home");
}

function prefillJoin(token: string): void {
  const parsed = decodePairingToken(token);
  if (!parsed) {
    setStatus("invalid pairing link", "error");
    return;
  }
  $<HTMLInputElement>("join-token").value = token;
  if (parsed.code) {
    void joinChannel();
  } else {
    setStatus("enter the 6-digit code to join", "ok");
    $<HTMLInputElement>("join-code").focus();
  }
}

async function openChannel(channelId: string): Promise<void> {
  if (activeChannelId === channelId && engine) {
    showView("session-panel");
    return;
  }
  teardownSession();
  const saved = await getChannel(channelId);
  if (!saved) {
    setStatus("unknown channel", "error");
    navigate("/channels");
    return;
  }
  showView("session-panel");
  try {
    const latest = await api.getLatest(channelId);
    await touchChannel(channelId);
    await startSession(
      channelId,
      saved.type,
      base64UrlToBytes(saved.secretB64),
      latest.members,
      latest.keyBindings,
      saved
    );
    if (latest.keyDistribution && engine)
      await engine.ingestKeyDistribution(latest.keyDistribution);
    if (latest.envelope && engine) await engine.applyIncoming(latest.envelope);
    updateSessionChrome();
  } catch (e) {
    log(String(e), "err");
    setStatus("channel unavailable — it may have expired", "error");
    await removeChannel(channelId);
    navigate("/channels");
  }
}

function teardownSession(): void {
  stopPolling();
  if (ws) {
    ws.close();
    ws = null;
  }
  engine = null;
  wsOpen = false;
  activeChannelId = "";
  activeSecret = null;
  activeChannel = null;
}

async function renderChannelsList(): Promise<void> {
  const list = $("channel-list");
  const chans = await listChannels();
  $("channels-empty").classList.toggle("hidden", chans.length > 0);
  list.innerHTML = "";
  for (const ch of chans) {
    const card = document.createElement("div");
    card.className = "channel-card";
    const main = document.createElement("div");
    main.className = "channel-main";
    const name = document.createElement("div");
    name.className = "channel-name";
    name.textContent = ch.label;
    const meta = document.createElement("div");
    meta.className = "channel-meta muted small";
    meta.textContent = `${ch.type === "group" ? "Group" : "1-to-1"} · ${
      ch.creator ? "creator" : "member"
    } · ${new Date(ch.createdAt).toLocaleDateString()}`;
    main.append(name, meta);

    const actions = document.createElement("div");
    actions.className = "channel-actions";
    const open = document.createElement("a");
    open.className = "button primary small";
    open.href = `#/c/${encodeURIComponent(ch.channelId)}`;
    open.textContent = "Open";
    const del = document.createElement("button");
    del.className = "button small danger";
    del.textContent = ch.creator ? "Expire" : "Forget";
    del.addEventListener("click", () => {
      void (ch.creator ? expireChannel(ch) : forgetChannel(ch.channelId));
    });
    actions.append(open, del);

    card.append(main, actions);
    list.appendChild(card);
  }
}

async function expireChannel(ch: SavedChannel): Promise<void> {
  try {
    const proof = await pairingHash(
      ch.channelId,
      base64UrlToBytes(ch.secretB64)
    );
    await api.expireChannel(ch.channelId, proof);
    log(`expired channel ${ch.channelId}`, "ok");
  } catch (e) {
    log(`expire failed: ${String(e)}`, "err");
  }
  await removeChannel(ch.channelId);
  if (activeChannelId === ch.channelId) teardownSession();
  await renderChannelsList();
}

async function forgetChannel(channelId: string): Promise<void> {
  await removeChannel(channelId);
  await renderChannelsList();
}

async function expireActiveChannel(): Promise<void> {
  if (!activeChannel) return;
  if (
    typeof confirm === "function" &&
    !confirm(`Expire "${activeChannel.label}" for all devices?`)
  )
    return;
  await expireChannel(activeChannel);
  navigate("/channels");
}

function defaultLabel(type: ChannelType): string {
  return `${type === "group" ? "Group" : "Channel"} · ${new Date().toLocaleDateString()}`;
}

/* ------------------------------- pairing --------------------------------- */

async function createChannel(): Promise<void> {
  const type = (document.querySelector<HTMLInputElement>(
    'input[name="ctype"]:checked'
  )?.value ?? "1-1") as ChannelType;
  const label =
    $<HTMLInputElement>("create-label").value.trim() || defaultLabel(type);
  try {
    const mat = await createPairingMaterial(identity.publicKeyRaw);
    await api.createChannel({
      channelId: mat.channelId,
      type,
      pairingSecretHash: mat.pairingSecretHash,
      device: selfDevice(),
    });
    const saved: SavedChannel = {
      channelId: mat.channelId,
      type,
      secretB64: bytesToBase64Url(mat.secret),
      creator: true,
      label,
      createdAt: Date.now(),
      lastOpenedAt: Date.now(),
    };
    await saveChannel(saved);
    const link = `${location.origin}/#/join/${mat.token}`;
    showPairResult({
      link,
      code: mat.code,
      qrToken: mat.qrToken,
      sas: mat.sas,
      hint: "Share the link + code (or QR), then open the channel below.",
      openChannelId: mat.channelId,
    });
    log(`created ${type} channel ${mat.channelId}`, "ok");
    if (mat.deviceFingerprint)
      log(`device fingerprint: ${mat.deviceFingerprint}`);
    await startSession(
      mat.channelId,
      type,
      mat.secret,
      [selfDevice()],
      {},
      saved
    );
  } catch (e) {
    setStatus("create failed", "error");
    log(String(e), "err");
  }
}

async function joinChannel(): Promise<void> {
  const raw = $<HTMLInputElement>("join-token").value.trim();
  const parsed = decodePairingToken(raw);
  if (!parsed) {
    setStatus("invalid pairing link or token", "error");
    log("invalid pairing token", "err");
    return;
  }
  const code =
    parsed.code ?? $<HTMLInputElement>("join-code").value.replace(/\D/g, "");
  if (code.length < 6) {
    setStatus("enter the 6-digit code from the other device", "error");
    log("missing 6-digit pairing code", "err");
    return;
  }
  try {
    const linkSecret = base64UrlToBytes(parsed.secret);
    const secret = await deriveEffectiveSecret(
      parsed.channelId,
      linkSecret,
      code
    );
    const proof = await pairingHash(parsed.channelId, secret);
    const binding = await computeKeyBinding(secret, identity.publicKeyRaw);
    const res = await api.join({
      channelId: parsed.channelId,
      device: selfDevice(),
      pairingProof: proof,
      keyBinding: binding,
    });
    const saved: SavedChannel = {
      channelId: parsed.channelId,
      type: res.type,
      secretB64: bytesToBase64Url(secret),
      creator: false,
      label: defaultLabel(res.type),
      createdAt: Date.now(),
      lastOpenedAt: Date.now(),
    };
    await saveChannel(saved);
    log(`joined channel ${parsed.channelId}`, "ok");
    await startSession(
      parsed.channelId,
      res.type,
      secret,
      res.members,
      res.keyBindings,
      saved
    );
    navigate(`/c/${parsed.channelId}`);
  } catch (e) {
    setStatus("join failed — wrong code or expired link", "error");
    log(String(e), "err");
  }
}

interface PairView {
  link?: string;
  code?: string;
  qrToken?: string;
  sas: string;
  hint: string;
  openChannelId?: string;
}

function showPairResult(v: PairView): void {
  $("pair-result").classList.remove("hidden");
  toggleRow("link-row", v.link, "token");
  toggleRow("code-row", v.code, "pair-code");
  $("sas").textContent = v.sas;
  $("pair-hint").textContent = v.hint;
  const qr = $("qr");
  if (v.qrToken) {
    try {
      qr.innerHTML = renderQrSvg(encodeQr(v.qrToken, "M"), { moduleSize: 5 });
      qr.classList.remove("hidden");
    } catch {
      qr.classList.add("hidden");
    }
  } else {
    qr.classList.add("hidden");
  }
  const meta = $("pair-result").querySelector(".pair-meta");
  let open = document.getElementById(
    "open-channel"
  ) as HTMLAnchorElement | null;
  if (v.openChannelId) {
    if (!open && meta) {
      open = document.createElement("a");
      open.id = "open-channel";
      open.className = "button primary full";
      meta.appendChild(open);
    }
    if (open) {
      open.textContent = "Open channel ›";
      open.href = `#/c/${encodeURIComponent(v.openChannelId)}`;
      open.classList.remove("hidden");
    }
  } else if (open) {
    open.classList.add("hidden");
  }
}

function toggleRow(
  rowId: string,
  value: string | undefined,
  textId: string
): void {
  const row = $(rowId);
  if (value) {
    $(textId).textContent = value;
    row.classList.remove("hidden");
  } else {
    row.classList.add("hidden");
  }
}

function copyToken(): void {
  const link = $("token").textContent ?? "";
  void navigator.clipboard?.writeText(link).then(
    () => log("link copied"),
    () => log("copy failed", "err")
  );
}

function copyCode(): void {
  const code = $("pair-code").textContent ?? "";
  void navigator.clipboard?.writeText(code).then(
    () => log("code copied"),
    () => log("copy failed", "err")
  );
}

/* ------------------------------- session --------------------------------- */

async function startSession(
  channelId: string,
  type: ChannelType,
  secret: Uint8Array,
  members: DeviceInfo[],
  keyBindings: Record<string, string>,
  saved?: SavedChannel
): Promise<void> {
  activeChannelId = channelId;
  activeSecret = secret;
  activeChannel = saved ?? activeChannel;
  const replay = await getReplayState(channelId);
  engine = new ChannelEngine(
    channelId,
    type,
    identity,
    secret,
    {
      onText: applyIncomingText,
      onImage: applyIncomingImage,
      onFileOffer: applyIncomingFileOffer,
      onFileRequest: provideFileData,
      onFile: applyIncomingFile,
      onMeta: applyIncomingMeta,
      onControl: applyIncomingControl,
      onError: (m) => log(m, "err"),
    },
    replay,
    () => void saveReplayState(channelId, replay)
  );
  await engine.setMembers(members, keyBindings);
  $("session-title").textContent = activeChannel?.label ?? "Channel";
  $("btn-expire").classList.toggle("hidden", !activeChannel?.creator);
  updateSessionChrome();

  $("session-sas").textContent = await sasCode(channelId, secret);

  ws = new WsClient(RELAY_BASE, channelId, identity.deviceId, {
    onMessage: handleServerMessage,
    onOpen: () => {
      wsOpen = true;
      stopPolling();
      setTransport("realtime");
    },
    onClose: () => {
      wsOpen = false;
      setTransport("polling");
      startPolling();
    },
  });
  ws.connect();
  // Fallback: if the socket never opens, poll.
  window.setTimeout(() => {
    if (!wsOpen) startPolling();
  }, 3000);

  await distributeKeysIfManager();
}

function handleServerMessage(msg: ServerToClient): void {
  void (async () => {
    if (!engine) return;
    switch (msg.t) {
      case "welcome": {
        const l = msg.latest;
        await engine.setMembers(l.members, l.keyBindings);
        if (l.keyDistribution)
          await engine.ingestKeyDistribution(l.keyDistribution);
        if (l.envelope) await engine.applyIncoming(l.envelope);
        updateSessionChrome();
        await distributeKeysIfManager();
        break;
      }
      case "event":
        await engine.applyIncoming(msg.envelope);
        break;
      case "keys":
        if (await engine.ingestKeyDistribution(msg.keyDistribution)) {
          log("received group key", "ok");
        }
        break;
      case "members":
        await engine.setMembers(msg.members, msg.keyBindings);
        updateSessionChrome();
        await distributeKeysIfManager();
        break;
      case "rejected":
        log(`push rejected: ${msg.reason}`);
        break;
      case "error":
        log(`server: ${msg.message}`, "err");
        if (/expired/i.test(msg.message)) {
          const id = activeChannelId;
          teardownSession();
          if (id) void removeChannel(id);
          setStatus("channel expired", "error");
          navigate("/channels");
        }
        break;
      default:
        break;
    }
  })();
}

/* ----------------------------- polling fallback -------------------------- */

function startPolling(): void {
  if (pollTimer !== null || !engine) return;
  const tick = async () => {
    if (!engine || wsOpen) return;
    try {
      const latest = await api.getLatest(activeChannelId);
      await engine.setMembers(latest.members, latest.keyBindings);
      if (latest.keyDistribution)
        await engine.ingestKeyDistribution(latest.keyDistribution);
      if (latest.envelope) await engine.applyIncoming(latest.envelope);
      updateSessionChrome();
      await distributeKeysIfManager();
    } catch (e) {
      log(`poll error: ${String(e)}`, "err");
    }
  };
  setTransport("polling");
  pollTimer = window.setInterval(() => void tick(), 2500);
  void tick();
}

function stopPolling(): void {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/* ------------------------------- sending --------------------------------- */

async function push(
  envelope: EncryptedEnvelope,
  keyDistribution?: KeyDistribution
): Promise<void> {
  const sentOverWs =
    ws?.send(
      keyDistribution
        ? { t: "push", envelope, keyDistribution }
        : { t: "push", envelope }
    ) ?? false;
  if (!sentOverWs) {
    const res = await api.push(activeChannelId, envelope, keyDistribution);
    if (!res.accepted) log(`push not accepted: ${res.reason ?? ""}`);
  }
}

async function sendText(): Promise<void> {
  const el = $<HTMLTextAreaElement>("text-input");
  const text = el.value;
  if (!text) return;
  await guardSend(async () => {
    const env = await engine!.encryptText(text);
    await push(env);
    log(`sent text (${text.length} chars)`, "ok");
  });
}

/** A tiny FNV-1a signature so we can cheaply tell whether the clipboard content
 *  changed without keeping (or comparing) whole megabytes. */
function quickSig(prefix: string, data: Uint8Array | string): string {
  const bytes = typeof data === "string" ? utf8ToBytes(data) : data;
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i++) {
    h ^= bytes[i]!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return `${prefix}:${bytes.length}:${h.toString(16)}`;
}

/** Best-effort plain-text fallback from an HTML fragment (jsdom-safe). */
function htmlToText(html: string): string {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent ?? "";
  } catch {
    return html.replace(/<[^>]*>/g, "");
  }
}

/**
 * Normalize any raster image to PNG so it round-trips reliably: browsers only
 * reliably *write* image/png to the clipboard, and the native agents expect
 * PNG. Handles "copy image" / screenshots that arrive as JPEG/GIF/WebP/BMP.
 * Best-effort — falls back to the original bytes if image decoding isn't
 * available (e.g. jsdom) or fails.
 */
async function toPngBytes(
  bytes: Uint8Array,
  mime: string
): Promise<{ bytes: Uint8Array; mime: string }> {
  if (mime === "image/png") return { bytes, mime };
  try {
    if (typeof createImageBitmap !== "function") return { bytes, mime };
    const src = new Blob([bytes as unknown as BlobPart], { type: mime });
    const bitmap = await createImageBitmap(src);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width || 1;
    canvas.height = bitmap.height || 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { bytes, mime };
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    const png = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/png")
    );
    if (!png) return { bytes, mime };
    return {
      bytes: new Uint8Array(await png.arrayBuffer()),
      mime: "image/png",
    };
  } catch {
    return { bytes, mime };
  }
}

/**
 * Read the OS clipboard and send whatever is there (point 1 + 3: text, rich
 * text, and images). `manual` = triggered by the button (report errors, ignore
 * the change guard); auto = triggered by focus/visibility (silent, de-duped).
 */
async function captureAndSend(manual: boolean): Promise<void> {
  if (!engine || !engine.canSend()) return;
  const clip = navigator.clipboard as Clipboard | undefined;

  // Older/permission-limited browsers: fall back to text-only readText().
  if (!clip || typeof clip.read !== "function") {
    if (manual && clip && typeof clip.readText === "function") {
      try {
        const text = await clip.readText();
        if (!text) return void log("clipboard is empty (or permission denied)");
        lastAutoSig = quickSig("t", `\u0000${text}`);
        await guardSend(async () => {
          await push(await engine!.encryptText(text));
          log("sent clipboard text", "ok");
        });
      } catch (e) {
        log(`clipboard read failed: ${String(e)}`, "err");
      }
    }
    return;
  }

  let items: ClipboardItem[];
  try {
    items = await clip.read();
  } catch (e) {
    if (manual) log(`clipboard read failed: ${String(e)}`, "err");
    return; // permission denied / not focused — stay quiet (point 1)
  }

  // Images take priority over any text alternative representation.
  for (const item of items) {
    const imgType = item.types.find((t) => t.startsWith("image/"));
    if (!imgType) continue;
    const blob = await item.getType(imgType);
    const raw = new Uint8Array(await blob.arrayBuffer());
    // De-dupe on the raw source so we don't re-encode the same image repeatedly.
    const sig = quickSig("i", raw);
    if (!manual && sig === lastAutoSig) return;
    const { bytes, mime } = await toPngBytes(raw, blob.type || imgType);
    if (bytes.length > SIZE_LIMITS.image) {
      if (manual) log("clipboard image exceeds 4 MB — not sent", "err");
      return;
    }
    lastAutoSig = sig;
    await guardSend(async () => {
      await push(await engine!.encryptImage(bytes, mime));
      log(
        `${manual ? "sent" : "auto-sent"} clipboard image (${fmtBytes(bytes.length)})`,
        "ok"
      );
    });
    return;
  }

  // Otherwise text — capture the rich (HTML) representation alongside plain.
  let plain = "";
  let html = "";
  for (const item of items) {
    if (!plain && item.types.includes("text/plain"))
      plain = await (await item.getType("text/plain")).text();
    if (!html && item.types.includes("text/html"))
      html = await (await item.getType("text/html")).text();
  }
  if (!plain && html) plain = htmlToText(html);
  if (!plain) {
    if (manual) log("clipboard is empty (or permission denied)");
    return;
  }
  if (utf8ToBytes(plain).length > SIZE_LIMITS.text) {
    if (manual) log("clipboard text exceeds 1 MB — not sent", "err");
    return;
  }
  const sig = quickSig("t", `${html}\u0000${plain}`);
  if (!manual && sig === lastAutoSig) return;
  lastAutoSig = sig;
  await guardSend(async () => {
    await push(await engine!.encryptText(plain, html || undefined));
    log(
      `${manual ? "sent" : "auto-sent"} clipboard text (${plain.length} chars)`,
      "ok"
    );
  });
}

async function sendFromClipboard(): Promise<void> {
  await captureAndSend(true);
}

function onFilePicked(ev: Event): void {
  const input = ev.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) void sendFile(file);
  input.value = "";
}

async function sendFile(file: File): Promise<void> {
  await guardSend(async () => {
    // Send a metadata-only OFFER (a "file promise") — no bytes are uploaded. The
    // bytes are streamed later, only if a peer requests this file.
    const bytes = new Uint8Array(await file.arrayBuffer());
    const { envelope, fileId } = await engine!.encryptFileOffer(
      bytes,
      file.name,
      file.type || "application/octet-stream"
    );
    outgoingFiles.set(fileId, file);
    await push(envelope);
    log(
      `offered ${file.name} (${fmtBytes(bytes.length)}) — sent on request`,
      "ok"
    );
  });
}

async function onPaste(ev: ClipboardEvent): Promise<void> {
  if (!engine) return;
  const target = ev.target as HTMLElement | null;
  if (target && (target.tagName === "TEXTAREA" || target.tagName === "INPUT")) {
    return; // let the user edit normally
  }
  const items = ev.clipboardData?.items;
  if (!items) return;
  for (const item of items) {
    if (item.kind === "file") {
      const file = item.getAsFile();
      if (!file) continue;
      if (item.type.startsWith("image/")) {
        const raw = new Uint8Array(await file.arrayBuffer());
        const { bytes, mime } = await toPngBytes(raw, item.type);
        lastAutoSig = quickSig("i", raw);
        await guardSend(async () => {
          await push(await engine!.encryptImage(bytes, mime));
          log(`sent pasted image (${fmtBytes(bytes.length)})`, "ok");
        });
      } else {
        await sendFile(file);
      }
      return;
    }
  }
  const text = ev.clipboardData?.getData("text/plain") ?? "";
  const html = ev.clipboardData?.getData("text/html") || undefined;
  const plain = text || (html ? htmlToText(html) : "");
  if (plain || html) {
    lastAutoSig = quickSig("t", `${html ?? ""}\u0000${plain}`);
    await guardSend(async () => {
      await push(await engine!.encryptText(plain, html));
      log("sent pasted text", "ok");
    });
  }
}

async function sendClear(): Promise<void> {
  await guardSend(async () => {
    await push(await engine!.encryptControl("clear_clipboard"));
    clearIncoming();
    void navigator.clipboard?.writeText("").catch(() => undefined);
    log("sent clear_clipboard", "ok");
  });
}

async function rotateKeys(): Promise<void> {
  if (!engine) return;
  const dist = await engine.maybeDistributeKeys(true);
  if (dist) {
    await push(dist.envelope, dist.keyDistribution);
    log("rotated group key", "ok");
  }
}

async function toggleLock(): Promise<void> {
  if (!engine) return;
  const command = engine.isLocked ? "unlock_channel" : "lock_channel";
  try {
    await push(await engine.encryptControl(command));
    engine.applyLocalControl({ type: "control", command, target: "channel" });
    updateSessionChrome();
    log(`sent ${command}`, "ok");
  } catch (e) {
    log(String(e), "err");
  }
}

async function leaveChannel(): Promise<void> {
  if (!engine) return;
  try {
    await push(
      await engine.encryptControl("leave_channel", engine.selfDeviceId)
    );
  } catch {
    /* best effort */
  }
  teardownSession();
  log("left channel", "ok");
  navigate("/channels");
}

/** Revoke another device: broadcast the control, remove + tombstone it locally,
 *  and (as manager) rotate the GSK so the removed device is locked out. */
async function revokeDevice(deviceId: string): Promise<void> {
  if (!engine || deviceId === engine.selfDeviceId) return;
  try {
    await push(await engine.encryptControl("revoke_device", deviceId));
    engine.applyLocalControl({
      type: "control",
      command: "revoke_device",
      target: deviceId,
    });
    await distributeKeysIfManager();
    updateSessionChrome();
    log(`revoked device ${deviceId}`, "ok");
  } catch (e) {
    log(String(e), "err");
  }
}

/** Spec §55: on being revoked, delete local keys + disconnect. A fresh identity
 *  is minted so the device can re-pair later as a brand-new device. */
async function wipeSelfAfterRevoke(): Promise<void> {
  teardownSession();
  try {
    await deleteIdentity();
    identity = await loadOrCreateIdentity();
    log("local identity keys wiped", "ok");
    setStatus(
      `revoked — identity wiped. New device ${identity.deviceId}`,
      "error"
    );
  } catch (e) {
    setStatus("revoked — failed to wipe local keys", "error");
    log(`key wipe failed: ${String(e)}`, "err");
  }
  navigate("/");
}

async function guardSend(fn: () => Promise<void>): Promise<void> {
  if (!engine) return;
  if (!engine.canSend()) {
    log(
      engine.isLocked
        ? "channel is locked — send disabled"
        : "cannot send yet — waiting for a paired device / group key",
      "err"
    );
    return;
  }
  try {
    await fn();
  } catch (e) {
    log(String(e), "err");
  }
}

async function distributeKeysIfManager(): Promise<void> {
  if (!engine) return;
  try {
    const dist = await engine.maybeDistributeKeys();
    if (dist) {
      await push(dist.envelope, dist.keyDistribution);
      log("distributed group key to members");
    }
  } catch (e) {
    log(`key distribution failed: ${String(e)}`, "err");
  }
}

/* ------------------------------ apply incoming --------------------------- */

function applyIncomingText(p: TextPayload): void {
  const box = $("incoming");
  box.classList.remove("muted");
  box.textContent = p.plain;
  lastAutoSig = quickSig("t", `${p.html ?? ""}\u0000${p.plain}`);
  void writeTextToClipboard(p.plain, p.html);
  log("clipboard updated (text) from a paired device", "ok");
}

function applyIncomingImage(p: ImagePayload, bytes: Uint8Array): void {
  const box = $("incoming");
  box.classList.remove("muted");
  const blob = new Blob([bytes as unknown as BlobPart], { type: p.mimeType });
  const url = URL.createObjectURL(blob);
  box.innerHTML = "";
  const img = document.createElement("img");
  img.src = url;
  img.onload = () => URL.revokeObjectURL(url);
  box.appendChild(img);
  lastAutoSig = quickSig("i", bytes);
  void writeImageToClipboard(blob);
  log("clipboard updated (image) from a paired device", "ok");
}

/** A file OFFER arrived (metadata only). Show a Download button; the bytes are
 *  fetched from the source only when the user clicks it. */
function applyIncomingFileOffer(offer: FileOfferPayload): void {
  const box = $("incoming");
  box.classList.remove("muted");
  box.innerHTML = "";
  const info = document.createElement("div");
  info.textContent = `File available: ${offer.filename} (${fmtBytes(offer.size)})`;
  const btn = document.createElement("button");
  btn.className = "primary";
  btn.textContent = "Download";
  btn.style.marginTop = "8px";
  btn.onclick = () => void requestOfferedFile(offer, btn);
  box.append(info, btn);
  log(
    `file available: ${offer.filename} (${fmtBytes(offer.size)}) — click Download to fetch`,
    "ok"
  );
}

/** Ask the source to stream a file's bytes (explicit user action). */
async function requestOfferedFile(
  offer: FileOfferPayload,
  btn: HTMLButtonElement
): Promise<void> {
  btn.disabled = true;
  btn.textContent = "Requesting…";
  try {
    await push(await engine!.encryptFileRequest(offer.fileId));
    log(`requested ${offer.filename} from the source`, "ok");
  } catch (e) {
    btn.disabled = false;
    btn.textContent = "Download";
    log(`request failed: ${(e as Error).message}`, "err");
  }
}

/** A peer requested a file we offered — stream its bytes on demand. */
async function provideFileData(fileId: string): Promise<void> {
  const file = outgoingFiles.get(fileId);
  if (!file) return; // not one of ours
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const envs = await engine!.encryptFileData(fileId, bytes);
    for (const env of envs) await push(env);
    log(
      `uploaded ${file.name} on request (${fmtBytes(bytes.length)}` +
        (envs.length > 1 ? `, ${envs.length} chunks` : "") +
        ")",
      "ok"
    );
  } catch (e) {
    log(`file upload failed: ${(e as Error).message}`, "err");
  }
}

/** The requested file's bytes fully arrived — trigger the download now. */
function applyIncomingFile(meta: FileMeta, bytes: Uint8Array): void {
  const blob = new Blob([bytes as unknown as BlobPart], {
    type: meta.mimeType,
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = meta.filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  log(`downloaded ${meta.filename} (${fmtBytes(bytes.length)})`, "ok");
}

function applyIncomingMeta(p: MetaPayload): void {
  log(`meta: ${p.event}${p.detail ? " (" + p.detail + ")" : ""}`);
}

function applyIncomingControl(p: ControlPayload): void {
  switch (p.command) {
    case "clear_clipboard":
      clearIncoming();
      void navigator.clipboard?.writeText("").catch(() => undefined);
      log("clipboard cleared by remote control", "ok");
      break;
    case "lock_channel":
      updateSessionChrome();
      log("channel locked by a remote device", "ok");
      break;
    case "unlock_channel":
      updateSessionChrome();
      log("channel unlocked by a remote device", "ok");
      break;
    case "revoke_device":
      if (p.target === engine?.selfDeviceId) {
        log(
          "this device was revoked — wiping local keys & disconnecting",
          "err"
        );
        ws?.close();
        stopPolling();
        void wipeSelfAfterRevoke();
      } else {
        updateSessionChrome();
        log(`device revoked: ${p.target}`);
        void distributeKeysIfManager(); // rotate GSK to lock out the removed device
      }
      break;
    case "leave_channel":
      updateSessionChrome();
      log(`a device left the channel: ${p.target}`);
      void distributeKeysIfManager(); // rotate GSK for the remaining members
      break;
    default:
      log(`control: ${p.command}`);
  }
}

async function writeImageToClipboard(blob: Blob): Promise<void> {
  try {
    const CI = (window as unknown as { ClipboardItem?: typeof ClipboardItem })
      .ClipboardItem;
    if (CI && navigator.clipboard && "write" in navigator.clipboard) {
      await navigator.clipboard.write([new CI({ [blob.type]: blob })]);
    }
  } catch {
    /* clipboard image write is best-effort (needs a user gesture / permission) */
  }
}

/** Write incoming text to the OS clipboard, preserving the rich (HTML)
 *  representation when present (point 3). Falls back to plain writeText. */
async function writeTextToClipboard(
  plain: string,
  html?: string
): Promise<void> {
  try {
    const CI = (window as unknown as { ClipboardItem?: typeof ClipboardItem })
      .ClipboardItem;
    const clip = navigator.clipboard;
    if (html && CI && clip && "write" in clip) {
      await clip.write([
        new CI({
          "text/plain": new Blob([plain], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return;
    }
    await clip?.writeText(plain);
  } catch {
    /* best-effort (needs a user gesture / permission) */
  }
}

/* --------------------------------- chrome -------------------------------- */

function updateSessionChrome(): void {
  if (!engine) return;
  const members = engine.getMembers();
  $("members").textContent =
    `${members.length} device${members.length === 1 ? "" : "s"}`;
  renderDeviceList();
  const roleEl = $("role");
  const rotateEl = $("btn-rotate");
  if (engine.type === "group" && engine.isManager()) {
    roleEl.classList.remove("hidden");
    rotateEl.classList.remove("hidden");
  } else {
    roleEl.classList.add("hidden");
    rotateEl.classList.add("hidden");
  }

  const locked = engine.isLocked;
  ($("btn-lock") as HTMLButtonElement).textContent = locked ? "Unlock" : "Lock";
  ($("btn-send-text") as HTMLButtonElement).disabled = locked;
  ($("btn-read-clip") as HTMLButtonElement).disabled = locked;
}

function setTransport(mode: "realtime" | "polling" | "connecting…"): void {
  $("transport").textContent = mode;
}

/** Render the member list with a Revoke action per other device (group manager
 *  only), so a compromised/retired device can be forcibly removed (spec §55). */
function renderDeviceList(): void {
  if (!engine) return;
  const list = $("device-list");
  list.innerHTML = "";
  const canRevoke =
    engine.type === "group" &&
    (engine.isManager() || (activeChannel?.creator ?? false));
  for (const m of engine.getMembers()) {
    const li = document.createElement("li");
    const isSelf = m.deviceId === engine.selfDeviceId;
    const label = document.createElement("span");
    label.className = "device-id";
    label.textContent = isSelf ? `${m.deviceId} (this device)` : m.deviceId;
    li.appendChild(label);
    if (!isSelf && canRevoke) {
      const btn = document.createElement("button");
      btn.className = "small danger";
      btn.textContent = "Revoke";
      btn.addEventListener("click", () => void revokeDevice(m.deviceId));
      li.appendChild(btn);
    }
    list.appendChild(li);
  }
}

function clearIncoming(): void {
  const box = $("incoming");
  box.classList.add("muted");
  box.textContent = "nothing yet";
}

function wireDropzone(): void {
  const dz = $("dropzone");
  const stop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  dz.addEventListener("dragover", (e) => {
    stop(e);
    dz.classList.add("drag");
  });
  dz.addEventListener("dragleave", (e) => {
    stop(e);
    dz.classList.remove("drag");
  });
  dz.addEventListener("drop", (e) => {
    stop(e);
    dz.classList.remove("drag");
    const file = e.dataTransfer?.files?.[0];
    if (file) void sendFile(file);
  });
}

/* --------------------------------- utils --------------------------------- */

function selfDevice(): DeviceInfo {
  return {
    deviceId: identity.deviceId,
    publicKey: identity.publicKeyB64,
    label: navigator.platform || "browser",
    joinedAt: Date.now(),
  };
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function setStatus(
  text: string,
  state: "idle" | "ok" | "error" = "idle"
): void {
  const el = $("status");
  el.textContent = text;
  el.dataset["state"] = state;
}

function log(message: string, cls?: "err" | "ok"): void {
  const el = $("log");
  const line = document.createElement("div");
  if (cls) line.className = cls;
  const time = new Date().toLocaleTimeString();
  line.textContent = `[${time}] ${message}`;
  el.prepend(line);
  while (el.childElementCount > 200) el.lastChild?.remove();
}

void main();
